import React from 'react';
import { TrendingUp, ArrowRight } from 'lucide-react';

export const ConversionAnalytics = ({ analytics }) => {
  if (!analytics) return null;
  const f = analytics.funnel || {};

  const funnelSteps = [
    { label: 'Submitted', value: f.submitted || 0, color: 'bg-blue-500' },
    { label: 'Interviewing', value: f.interviewing || 0, color: 'bg-indigo-500' },
    { label: 'Offers', value: f.offers || 0, color: 'bg-amber-500' },
    { label: 'Placed', value: f.placed || 0, color: 'bg-emerald-500' },
  ];
  const maxVal = Math.max(...funnelSteps.map(s => s.value), 1);

  return (
    <div className="space-y-4 mb-6" data-testid="conversion-analytics">
      {/* Funnel */}
      <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
        <div className="flex items-center gap-2 mb-4">
          <TrendingUp className="h-4 w-4 text-[#D4AF37]" />
          <h3 className="text-sm font-bold text-[#0A1F44]">Conversion Funnel</h3>
          <span className="text-[10px] text-gray-400">{analytics.total_submissions} total submissions</span>
        </div>
        <div className="flex items-end gap-2 h-24 mb-3">
          {funnelSteps.map((step, i) => (
            <div key={i} className="flex-1 flex flex-col items-center gap-1">
              <span className="text-lg font-bold text-[#0A1F44]">{step.value}</span>
              <div className={`w-full rounded-t-lg ${step.color} transition-all`}
                style={{ height: `${Math.max((step.value / maxVal) * 100, 8)}%` }}></div>
              <span className="text-[9px] text-gray-500 font-medium">{step.label}</span>
            </div>
          ))}
        </div>
        <div className="flex items-center justify-center gap-4 pt-3 border-t border-gray-100">
          <div className="text-center">
            <p className="text-sm font-bold text-indigo-600">{f.sub_to_interview || 0}%</p>
            <p className="text-[8px] text-gray-400 uppercase">Sub <ArrowRight className="h-2 w-2 inline" /> Interview</p>
          </div>
          <div className="text-center">
            <p className="text-sm font-bold text-amber-600">{f.interview_to_offer || 0}%</p>
            <p className="text-[8px] text-gray-400 uppercase">Interview <ArrowRight className="h-2 w-2 inline" /> Offer</p>
          </div>
          <div className="text-center">
            <p className="text-sm font-bold text-emerald-600">{f.offer_to_placed || 0}%</p>
            <p className="text-[8px] text-gray-400 uppercase">Offer <ArrowRight className="h-2 w-2 inline" /> Placed</p>
          </div>
        </div>
      </div>

      {/* Breakdowns */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* By Score Range */}
        <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
          <p className="text-[10px] text-gray-500 uppercase tracking-wider font-medium mb-2">By Score Range</p>
          {Object.entries(analytics.by_score_range || {}).map(([k, v]) => (
            <div key={k} className="flex items-center justify-between py-1.5 border-b border-gray-50 last:border-0">
              <span className="text-xs text-[#0A1F44] font-medium">{k.replace(/_/g, ' ').replace('high ', 'High (').replace('medium ', 'Medium (').replace('low ', 'Low (') + ')'}</span>
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-600">{v.submitted} sub</span>
                {v.placed > 0 && <span className="text-xs text-emerald-600 font-bold">{v.placed} placed</span>}
              </div>
            </div>
          ))}
        </div>

        {/* By Playbook */}
        <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
          <p className="text-[10px] text-gray-500 uppercase tracking-wider font-medium mb-2">By Playbook</p>
          {Object.entries(analytics.by_playbook || {}).length === 0 ? (
            <p className="text-xs text-gray-400">No data yet</p>
          ) : Object.entries(analytics.by_playbook || {}).map(([k, v]) => (
            <div key={k} className="flex items-center justify-between py-1.5 border-b border-gray-50 last:border-0">
              <span className="text-xs text-[#0A1F44] font-medium truncate max-w-[120px]">{k}</span>
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-600">{v.submitted} sub</span>
                {v.placed > 0 && <span className="text-xs text-emerald-600 font-bold">{v.placed} placed</span>}
              </div>
            </div>
          ))}
        </div>

        {/* By Vertical */}
        <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
          <p className="text-[10px] text-gray-500 uppercase tracking-wider font-medium mb-2">By Role Type</p>
          {Object.entries(analytics.by_vertical || {}).length === 0 ? (
            <p className="text-xs text-gray-400">No data yet</p>
          ) : Object.entries(analytics.by_vertical || {}).map(([k, v]) => (
            <div key={k} className="flex items-center justify-between py-1.5 border-b border-gray-50 last:border-0">
              <span className="text-xs text-[#0A1F44] font-medium capitalize">{k.replace(/_/g, ' ')}</span>
              <div className="flex items-center gap-3 text-[10px]">
                <span className="text-gray-600">{v.submitted} sub</span>
                <span className="text-indigo-600">{v.interviewing} int</span>
                <span className="text-emerald-600 font-bold">{v.placed} placed</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
